import React from 'react'

const BatteriehinweisPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8 text-center">Batteriehinweis</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          <div className="bg-green-50 border-l-4 border-green-400 p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800 font-medium">
                  Wichtige Informationen zur ordnungsgemäßen Batterieentsorgung
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-4">Gesetzliche Hinweise zur Batterieentsorgung</h2>
              <p className="text-gray-700 leading-relaxed">
                Im Zusammenhang mit dem Vertrieb von Batterien oder mit der Lieferung von Geräten, die Batterien enthalten, 
                sind wir verpflichtet, Sie auf folgendes hinzuweisen:
              </p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-blue-800">Ihre Rückgabepflicht als Endnutzer</h3>
              <p className="text-gray-700 mb-4">
                <strong>Sie sind zur Rückgabe gebrauchter Batterien als Endnutzer gesetzlich verpflichtet.</strong>
              </p>
              <p className="text-gray-700">
                Sie können Altbatterien, die wir als Neubatterien im Sortiment führen oder geführt haben, 
                <strong className="text-blue-800"> unentgeltlich an unserem Versandlager</strong> zurückgeben:
              </p>
              
              <div className="mt-4 p-4 bg-white rounded border">
                <p className="font-semibold text-gray-800">Rückgabe-Adresse:</p>
                <p className="text-gray-700">
                  PhoneMedic<br />
                  Konrad Blersch<br />
                  Wahlenstraße 27<br />
                  93047 Regensburg
                </p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Bedeutung der Batteriesymbole</h3>
              <p className="text-gray-700 mb-4">
                Die auf den Batterien abgebildeten Symbole haben folgende Bedeutung:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 border rounded-lg">
                  <div className="flex-shrink-0 w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">🗑️ Durchgekreuzte Mülltonne</h4>
                    <p className="text-gray-700">
                      Das Symbol der durchgekreuzten Mülltonne bedeutet, dass die Batterie 
                      <strong className="text-indigo-600"> nicht in den Hausmüll</strong> gegeben werden darf.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-orange-50">
                    <h4 className="font-semibold text-orange-800 mb-2">Pb = Blei</h4>
                    <p className="text-sm text-gray-700">
                      Batterie enthält mehr als <br />
                      <strong>0,004 Masseprozent Blei</strong>
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg bg-indigo-50">
                    <h4 className="font-semibold text-indigo-800 mb-2">Cd = Cadmium</h4>
                    <p className="text-sm text-gray-700">
                      Batterie enthält mehr als <br />
                      <strong>0,002 Masseprozent Cadmium</strong>
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg bg-indigo-50">
                    <h4 className="font-semibold text-indigo-800 mb-2">Hg = Quecksilber</h4>
                    <p className="text-sm text-gray-700">
                      Batterie enthält mehr als <br />
                      <strong>0,0005 Masseprozent Quecksilber</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Warum ist die ordnungsgemäße Entsorgung wichtig?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span>Schutz der Umwelt vor schädlichen Chemikalien</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span>Wiederverwertung wertvoller Rohstoffe</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span>Einhaltung gesetzlicher Bestimmungen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span>Verantwortungsvoller Umgang mit Ressourcen</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-gray-800 font-medium">
                <span className="text-yellow-600">⚠️</span> Bitte beachten Sie die vorstehenden Hinweise.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Bei Fragen zur Batterieentsorgung kontaktieren Sie uns gerne unter: 
                <a href="mailto:service@phone-medic.de" className="text-indigo-600 hover:underline ml-1">
                  service@phone-medic.de
                </a>
              </p>
            </div>
          </div>
          
          <div className="border-t pt-6">
            <p className="text-sm text-gray-600">
              Stand: Januar 2025 | PhoneMedic - Konrad Blersch<br />
              Gemäß Batteriegesetz (BattG) und EU-Batterierichtlinie
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BatteriehinweisPage